import { readItemField } from '../compile/item-field-reader';
import { planInventorySizeEnchantment } from '../compile/inventory-delivery-20';
import type { Row } from '../spawn/row-plan';

export interface InventoryTemplateSources { enchantment_templates: Row[]; enchantment_template_enchantments: Row[] }
export interface InventoryTemplateInsert { kind: 'template' | 'member'; row: Row }
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const field = (row: Row, key: string): unknown => readItemField(row, key);

/** A request-local composer: saved signatures and earlier proposals share the same lookup. */
export function createInventoryTemplateComposer(sources: InventoryTemplateSources, uuid: () => string,
    append: (insert: InventoryTemplateInsert) => void) {
    const templates = [...sources.enchantment_templates], members = [...sources.enchantment_template_enchantments];
    const heldMembers = (id: string): Row[] => members.filter(row => upper(field(row, 'enchantment_template_id')) === upper(id)
        && !Number(field(row, 'enchantment_template_enchantment_deleted') ?? 0));
    const signature = (list: Row[], ordered: boolean): string => {
        const groups = new Map<string, string[]>();
        for (const row of list) {
            const type = String(field(row, 'enchantment_type')), value = String(field(row, 'enchantment_item_id'));
            const values = groups.get(type) ?? []; values.push(value); groups.set(type, values);
        }
        return JSON.stringify([...groups].sort(([a], [b]) => a.localeCompare(b))
            .map(([type, values]) => [type, ordered ? values : values.sort()]));
    };
    const save = (parts: Row[], ordered = false): string => {
        const key = signature(parts, ordered);
        const saved = templates.find(row => !Number(field(row, 'enchantment_template_deleted') ?? 0)
            && signature(heldMembers(String(field(row, 'enchantment_template_id'))), ordered) === key);
        if (saved) return String(field(saved, 'enchantment_template_id'));
        const id = uuid(), template = { enchantment_template_id: id, enchantment_template_name: '' };
        templates.push(template); append({ kind: 'template', row: template });
        for (const part of parts) {
            const row = { enchantment_id: uuid(), enchantment_template_id: id, enchantment_type: String(field(part, 'enchantment_type')),
                enchantment_item_id: String(field(part, 'enchantment_item_id')), enchantment_name: '' };
            members.push(row); append({ kind: 'member', row });
        }
        return id;
    };
    return { save, compose(selected: string, size: string): string {
        const proposal = planInventorySizeEnchantment(selected, size,
            { enchantment_templates: templates, enchantment_template_enchantments: members }, uuid);
        if (proposal.authoring) {
            const template = { enchantment_template_id: proposal.templateId, enchantment_template_name: proposal.authoring.name };
            templates.push(template); append({ kind: 'template', row: template });
            for (const part of proposal.authoring.members) {
                const row = { enchantment_id: uuid(), enchantment_template_id: proposal.templateId,
                    enchantment_type: part.type, enchantment_item_id: part.value, enchantment_name: '' };
                members.push(row); append({ kind: 'member', row });
            }
        }
        return proposal.templateId;
    } };
}
